import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InfoIcon from "@mui/icons-material/Info";
import { useNavigate } from "react-router-dom";
import JSZip from "jszip";
import scriptRunnerV2Service from "../../services/scriptRunnerV2.service";
import useScriptPermissions from "./hooks/useScriptPermissions";

const REFERENCE_TEMPLATE = {
  title: "Give_Title_U_NEED",
  description: "Describe_UR_File",
  fields: [
    {
      name: "n1",
      label: "val",
      type: "type",
      required: true,
    },
    {
      name: "n2",
      label: "val",
      type: "type",
      default: true,
    },
    {
      name: "n-1",
      label: "val",
      type: "type",
      required: true,
    },
  ],
};

// interns scriptrunner - Component for uploading new scripts or new versions (V2)
const UploadScriptsV2 = () => {
  const navigate = useNavigate();
  const { canWrite } = useScriptPermissions();

  // interns scriptrunner - redirect if no write permission
  useEffect(() => {
    if (!canWrite) {
      navigate("/script-runner-V2");
    }
  }, [canWrite, navigate]);

  // interns scriptrunner - UI state
  const [loading, setLoading] = useState(false);
  const [fetchingScripts, setFetchingScripts] = useState(false);
  const [scripts, setScripts] = useState([]);
  const [uploadType, setUploadType] = useState("new"); // "new" or "version"
  const [incrementType, setIncrementType] = useState("patch"); // "patch", "minor", "major"

  // interns scriptrunner - Form state
  const [formData, setFormData] = useState({
    scriptId: "",
    name: "",
    description: "",
    version: "", // empty means auto-increment
    entrypoint: "main.py",
    inputSchema: "",
    changeSummary: "",
  });
  const [file, setFile] = useState(null);
  const [zipHasTemplate, setZipHasTemplate] = useState(false);
  const [isValidatingZip, setValidatingZip] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchScripts();
  }, []);

  const fetchScripts = async () => {
    try {
      setFetchingScripts(true);
      const data = await scriptRunnerV2Service.getAllScripts();
      setScripts(data);
    } catch (err) {
      console.error("Failed to fetch scripts", err);
    } finally {
      setFetchingScripts(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // interns scriptrunner - if selecting an existing script, prefill entrypoint if possible
    if (name === "scriptId" && value) {
      const selected = scripts.find((s) => s._id === value);
      if (selected) {
        // Entrypoint usually doesn't change much, could fetch last version here if needed
      }
    }
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setValidatingZip(true);
      setZipHasTemplate(false);

      try {
        const zip = new JSZip();
        const content = await zip.loadAsync(selectedFile);

        // Check if any file in the zip is template.json (handles nested paths too)
        const hasTemplate = Object.keys(content.files).some((fileName) =>
          fileName.endsWith("template.json"),
        );

        setZipHasTemplate(hasTemplate);
        console.log(
          `interns scriptrunner - ZIP validation: hasTemplate=${hasTemplate}`,
        );
      } catch (err) {
        console.error("interns scriptrunner - Error reading ZIP", err);
        setSnackbar({
          open: true,
          message:
            "Failed to read ZIP file. Please ensure it's a valid archive.",
          severity: "error",
        });
      } finally {
        setValidatingZip(false);
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // interns scriptrunner - Logic to determine if the UI schema is valid and has fields
  const getUiSchemaInfo = () => {
    if (!formData.inputSchema || formData.inputSchema.trim() === "")
      return { isValid: false, hasFields: false };
    try {
      const parsed = JSON.parse(formData.inputSchema);
      const hasFields = Object.keys(parsed).length > 0;
      return { isValid: true, hasFields };
    } catch (e) {
      return { isValid: false, hasFields: false };
    }
  };

  const uiSchemaInfo = getUiSchemaInfo();

  // interns scriptrunner - Logic to determine if upload is allowed
  // Requirement: template.json must be in UI (valid & >=1 field) or ZIP
  const isTemplatePresent = zipHasTemplate || uiSchemaInfo.hasFields;

  // For new scripts, we MUST have a template.
  // For existing versions, we also enforce it to ensure S3 consistency as requested.
  const canUpload =
    !loading &&
    !isValidatingZip &&
    file &&
    isTemplatePresent &&
    (uploadType === "new" ? formData.name : formData.scriptId);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      setSnackbar({
        open: true,
        message: "Please select a ZIP file to upload",
        severity: "error",
      });
      return;
    }

    if (!isTemplatePresent) {
      setSnackbar({
        open: true,
        message:
          "Mandatory template.json missing. Please provide it in UI or include it in the ZIP.",
        severity: "error",
      });
      return;
    }

    setLoading(true);
    try {
      let finalScriptId = formData.scriptId;

      // interns scriptrunner - Step 1: Create the script metadata entry if it's a new script
      if (uploadType === "new") {
        const script = await scriptRunnerV2Service.createScript({
          name: formData.name,
          description: formData.description,
        });
        finalScriptId = script._id;
      }

      // interns scriptrunner - Step 2: Prepare FormData for the version upload
      const uploadData = new FormData();
      uploadData.append("file", file);
      if (formData.version) uploadData.append("version", formData.version);
      uploadData.append("entrypoint", formData.entrypoint);
      if (formData.inputSchema)
        uploadData.append("inputSchema", formData.inputSchema);
      uploadData.append(
        "changeSummary",
        formData.changeSummary ||
          (uploadType === "new" ? "Initial version upload" : "Update version"),
      );

      // interns scriptrunner - add semantic flags
      if (uploadType === "version") {
        uploadData.append("isMajorUpdate", incrementType === "major");
        uploadData.append("isMinorUpdate", incrementType === "minor");
      }

      // interns scriptrunner - Step 3: Upload the script version
      await scriptRunnerV2Service.uploadVersion(finalScriptId, uploadData);

      setSnackbar({
        open: true,
        message: "Script uploaded successfully!",
        severity: "success",
      });

      setTimeout(() => {
        navigate("/script-runner-V2/view");
      }, 1500);
    } catch (error) {
      console.error("interns scriptrunner - Upload failed", error);
      setSnackbar({
        open: true,
        message: `Upload failed: ${error.response?.data?.message || error.message}`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/script-runner-V2")}
          disabled={loading}
          sx={{
            color: "#28BCBB",
            backgroundColor: "#f0f9f9",
            border: "1px solid rgba(40, 188, 187, 0.3)",
            borderRadius: "8px",
            padding: "6px 14px",
            fontWeight: 600,
            textTransform: "none",
            "&:hover": { backgroundColor: "#e6f0f1" },
          }}
        >
          Back to Dashboard
        </Button>
      </Box>

      <Paper
        sx={{ p: 4, maxWidth: 1100, mx: "auto", borderRadius: 3 }}
        elevation={3}
      >
        <Typography
          variant="h5"
          gutterBottom
          sx={{ fontWeight: "bold", color: "#1d9f9f", mb: 3 }}
        >
          Upload Script / Version (V2)
        </Typography>

        <Grid container spacing={4}>
          {/* Left Column: Upload Form */}
          <Grid item xs={12} md={7}>
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <RadioGroup
                row
                value={uploadType}
                onChange={(e) => setUploadType(e.target.value)}
              >
                <FormControlLabel
                  value="new"
                  control={<Radio color="primary" />}
                  label="New Script"
                />
                <FormControlLabel
                  value="version"
                  control={<Radio color="primary" />}
                  label="New Version for Existing Script"
                />
              </RadioGroup>
            </FormControl>

            <Divider sx={{ mb: 4 }} />

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {uploadType === "new" ? (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Script Name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="e.g. Daily Data Export"
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Description"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="What does this script do?"
                        disabled={loading}
                      />
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel id="script-select-label">
                        Select Script
                      </InputLabel>
                      <Select
                        labelId="script-select-label"
                        name="scriptId"
                        value={formData.scriptId}
                        onChange={handleInputChange}
                        label="Select Script"
                        disabled={loading || fetchingScripts}
                      >
                        {fetchingScripts ? (
                          <MenuItem disabled>
                            <CircularProgress size={20} sx={{ mr: 1 }} />{" "}
                            Loading scripts...
                          </MenuItem>
                        ) : (
                          scripts.map((s) => (
                            <MenuItem key={s._id} value={s._id}>
                              {s.name}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {uploadType === "version" && (
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: "bold" }}
                    >
                      Increment Type (Semantic Versioning)
                    </Typography>
                    <RadioGroup
                      row
                      value={incrementType}
                      onChange={(e) => setIncrementType(e.target.value)}
                    >
                      <FormControlLabel
                        value="patch"
                        control={<Radio size="small" />}
                        label="Patch (0.0.x)"
                      />
                      <FormControlLabel
                        value="minor"
                        control={<Radio size="small" />}
                        label="Minor (0.x.0)"
                      />
                      <FormControlLabel
                        value="major"
                        control={<Radio size="small" />}
                        label="Major (x.0.0)"
                      />
                    </RadioGroup>
                  </Grid>
                )}

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Custom Version (Optional)"
                    name="version"
                    value={formData.version}
                    onChange={handleInputChange}
                    placeholder="e.g. 1.2.3 (leave blank for auto)"
                    disabled={loading}
                    helperText="Defaults to 1.0.0 for new scripts"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Entrypoint"
                    name="entrypoint"
                    value={formData.entrypoint}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g. main.py"
                    disabled={loading}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Change Summary"
                    name="changeSummary"
                    value={formData.changeSummary}
                    onChange={handleInputChange}
                    placeholder="What changed in this version?"
                    disabled={loading}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    Input Schema (template.json)
                    {formData.inputSchema && (
                      <Box
                        component="span"
                        sx={{ ml: 1, display: "flex", alignItems: "center" }}
                      >
                        {uiSchemaInfo.isValid ? (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "green",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            ✓ Valid JSON{" "}
                            {uiSchemaInfo.hasFields
                              ? `(${Object.keys(JSON.parse(formData.inputSchema)).length} fields)`
                              : "(Empty)"}
                          </Typography>
                        ) : (
                          <Typography variant="caption" sx={{ color: "red" }}>
                            ✗ Invalid JSON
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    name="inputSchema"
                    value={formData.inputSchema}
                    onChange={handleInputChange}
                    placeholder={`{\n  "param_name": {\n    "type": "string",\n    "label": "Parameter Name"\n  }\n}`}
                    disabled={loading}
                    InputProps={{
                      sx: {
                        fontFamily: "'Fira Code', 'Courier New', monospace",
                        fontSize: "13px",
                        backgroundColor: "#1e1e1e",
                        color: "#d4d4d4",
                        borderRadius: "8px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#333",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#444",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#1d9f9f",
                        },
                        "& textarea": {
                          scrollbarWidth: "thin",
                          "&::-webkit-scrollbar": { width: "8px" },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "#333",
                            borderRadius: "4px",
                          },
                        },
                      },
                    }}
                    helperText={
                      !zipHasTemplate && !uiSchemaInfo.hasFields
                        ? "Mandatory: Please provide at least one field in the JSON schema or include template.json in ZIP."
                        : "JSON schema is ready. This will be injected into the script ZIP."
                    }
                    error={
                      formData.inputSchema.trim() !== "" &&
                      (!uiSchemaInfo.isValid || !uiSchemaInfo.hasFields)
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      border: "2px dashed #ccc",
                      borderRadius: 2,
                      p: 3,
                      textAlign: "center",
                      bgcolor: "#fafafa",
                    }}
                  >
                    <input
                      accept=".zip"
                      style={{ display: "none" }}
                      id="script-file-upload"
                      type="file"
                      onChange={handleFileChange}
                      disabled={loading}
                    />
                    <label htmlFor="script-file-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={
                          isValidatingZip ? (
                            <CircularProgress size={20} />
                          ) : (
                            <CloudUploadIcon />
                          )
                        }
                        disabled={loading || isValidatingZip}
                      >
                        {isValidatingZip
                          ? "Validating ZIP..."
                          : "Select ZIP File"}
                      </Button>
                    </label>
                    {file && (
                      <Box sx={{ mt: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: "green", fontWeight: 500 }}
                        >
                          Selected: {file.name}
                        </Typography>
                        {zipHasTemplate ? (
                          <Typography
                            variant="caption"
                            sx={{ color: "#1d9f9f", display: "block" }}
                          >
                            ✓ template.json found in ZIP
                          </Typography>
                        ) : (
                          <Typography
                            variant="caption"
                            sx={{ color: "#d32f2f", display: "block" }}
                          >
                            ✗ template.json NOT found in ZIP
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </Grid>

                {file && !isTemplatePresent && (
                  <Grid item xs={12}>
                    <Alert
                      severity="warning"
                      icon={<InfoIcon fontSize="inherit" />}
                      sx={{ borderRadius: 2 }}
                    >
                      <Typography variant="body2">
                        <strong>Missing Input Schema:</strong> Every script
                        requires a <code>template.json</code>. Please either
                        include it in your ZIP file or paste the JSON schema in
                        the field above.
                      </Typography>
                    </Alert>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={!canUpload}
                    startIcon={
                      loading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : null
                    }
                    sx={{
                      py: 1.5,
                      fontWeight: "bold",
                      background: canUpload ? "#1d9f9f" : "#ccc",
                      "&:hover": { background: canUpload ? "#158a8a" : "#ccc" },
                    }}
                  >
                    {loading
                      ? "Uploading..."
                      : uploadType === "new"
                        ? "Create & Upload Script"
                        : "Upload New Version"}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Grid>

          {/* Right Column: Reference Template */}
          <Grid
            item
            xs={12}
            md={5}
            sx={{
              display: "flex",
              minWidth: 0, // 🔥 VERY IMPORTANT
            }}
          >
            <Box
              sx={{
                backgroundColor: "#f5f5f5",
                p: 3,
                borderRadius: 2,
                border: "1px solid #e0e0e0",

                display: "flex",
                flexDirection: "column",
                flex: 1,
                minWidth: 0, // 🔥 VERY IMPORTANT
                overflow: "hidden", // 🔥 prevents child overflow leak
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: "bold",
                  mb: 2,
                  color: "#555",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <InfoIcon sx={{ mr: 1, color: "#1d9f9f" }} />
                Reference template.json
              </Typography>

              <Typography variant="body2" sx={{ mb: 2, color: "#666" }}>
                Use this structure as a reference for your input schema. The{" "}
                <code>fields</code> array defines the inputs users will see in
                the UI.
              </Typography>

              {/* CODE BOX */}
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "#1e1e1e",
                  borderRadius: 2,

                  flex: 1,
                  minWidth: 0, // 🔥 critical
                  maxWidth: "100%", // 🔥 prevent expansion

                  overflowX: "auto", // horizontal scroll if needed
                  overflowY: "auto",
                  maxHeight: "500px",
                }}
              >
                <pre
                  style={{
                    margin: 0,
                    color: "#d4d4d4",
                    fontFamily: "'Fira Code', monospace",
                    fontSize: "12px",
                    lineHeight: 1.5,

                    whiteSpace: "pre", // keep formatting
                    maxWidth: "100%", // 🔥 prevent breaking grid
                  }}
                >
                  {JSON.stringify(REFERENCE_TEMPLATE, null, 2)}
                </pre>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="caption"
                  sx={{ color: "#888", fontStyle: "italic" }}
                >
                  * Tip: You can copy and paste the reference into the editor
                  and modify it.
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UploadScriptsV2;
